import { formatRelativeTime } from '@moussenaires/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { notificationsApi } from '../api/notifications.js';
import { useClickOutside } from '../hooks/useClickOutside.js';
import './NotificationBell.css';

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  useClickOutside(panelRef, () => setOpen(false));
  const queryClient = useQueryClient();

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsApi.list({ page: 1, pageSize: 20 }),
    enabled: open,
  });

  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const unread = unreadQuery.data?.count ?? 0;

  return (
    <div className="notification-bell" ref={panelRef}>
      <button
        type="button"
        className="btn btn-ghost btn-icon notification-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('nav.notifications')}
      >
        <Bell size={19} />
        {unread > 0 && <span className="notification-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="notification-panel" role="menu">
          <div className="notification-panel-header">
            <h3>{t('notifications.title')}</h3>
            {unread > 0 && (
              <button
                type="button"
                className="btn btn-ghost notification-mark-all"
                onClick={() => markAllRead.mutate()}
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>
          <div className="notification-list scrollbar-thin">
            {listQuery.isLoading && <p className="notification-empty">{t('common.loading')}</p>}
            {listQuery.data?.items.length === 0 && (
              <p className="notification-empty">{t('notifications.empty')}</p>
            )}
            {listQuery.data?.items.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${notification.readAt ? '' : 'notification-item-unread'}`}
              >
                <p className="notification-text">
                  {t(`notifications.${notification.type}`, {
                    actor: notification.actor?.displayName ?? '',
                    subject: notification.subject ?? '',
                    defaultValue: notification.subject ?? notification.type,
                  })}
                </p>
                <time className="notification-time" dateTime={notification.createdAt}>
                  {formatRelativeTime(notification.createdAt, i18n.language as 'fr' | 'en')}
                </time>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
